'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { startTransition } from 'react';
import { flushSync } from 'react-dom';
import { buildRequestSource, stringifySource } from 'utils';
import { formatDashedYYYYMMDD } from 'utils/DateFormats';
import { makeHeroRoutesCmsKey, makePlaceImagesCmsKey } from '~/components/TripOverview/overviewPage/HydratedOverview';
import { preserveScrollOnEnter } from '~/components/TripOverview/ScrollToTopOnEnter';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { api } from '~/libs/api';
import type { TripOverviewLeg } from '~/services/engine-ts';
import { useTripBookingStore } from '~/stores/tpv2/tripBookingStore';
import type { CUSTOMER_INFO_MESSAGE, JOURNEY_EDIT_ERROR } from '~/types/Journey.types';
import { toCustomerInfoMessages } from '~/utils/originDateShiftNotice';
import { useJourneyEditErrorMessage } from './useJourneyEditErrorMessage';
import { REWRITTEN_TRIP_ID_HISTORY_KEY, useTripId } from './useTripId';
type UpdateTripValues = {
  closest_city: string;
  startDate: Date;
  endDate: Date | null;
  adult_count: number;
  child_count: number;
  childAges: number[];
  room_count: number;
  duration: number;
};
export const useUpdateTrip = sourceName => {
  const $ = _c(21);
  const tripId = useTripId();
  const router = useRouter();
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  const setActiveTripId = useTripBookingStore(_temp);
  const tFeedback = useTranslations("content.feedback");
  const getEditErrorMessage = useJourneyEditErrorMessage();
  let t0;
  if ($[0] !== tripId) {
    t0 = {
      tripId
    };
    $[0] = tripId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!tripId;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t1,
      keepPreviousData: true
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: tripOverview
  } = api.trip.getOverview.useQuery(t0, t2);
  let t3;
  if ($[4] !== tripId) {
    t3 = {
      tripId
    };
    $[4] = tripId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!tripId;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      ...TRPC_SETTINGS.QUERY_NO_REFETCH,
      enabled: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: t6
  } = api.cms.origins.useQuery(t3, t5);
  let t7;
  if ($[8] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const origins = t7;
  let t8;
  if ($[10] !== getEditErrorMessage || $[11] !== origins || $[12] !== queryClient || $[13] !== router || $[14] !== setActiveTripId || $[15] !== sourceName || $[16] !== tFeedback || $[17] !== tripId || $[18] !== tripOverview || $[19] !== utils) {
    t8 = async overrides => {
      if (!tripOverview) {
        return {
          error: tFeedback("error_generic")
        };
      }
      const mixes = tripOverview.pax.travellerMixes;
      const currentChildAges = mixes.flatMap(_temp2);
      const firstLeg = tripOverview.items.find(_temp3);
      const firstItem = tripOverview.items[0];
      const lastItem = tripOverview.items[tripOverview.items.length - 1];
      const tripStartDate = new Date(firstItem?.startDate ?? "");
      const tripEndDate = new Date(lastItem?.endDate ?? "");
      const values = {
        closest_city: firstLeg?.origin.name ?? "",
        startDate: tripStartDate,
        endDate: tripEndDate,
        adult_count: mixes.reduce(_temp4, 0),
        child_count: currentChildAges.length,
        childAges: currentChildAges,
        room_count: tripOverview.pax.numberOfRooms,
        duration: differenceInCalendarDays(tripEndDate, tripStartDate) + 1,
        ...overrides
      };
      const childAgesAsNumbers = values.childAges.slice(0, values.child_count).filter(_temp5);
      const originId = origins.find(o => o.locationName === values.closest_city)?.id ?? String(firstLeg?.origin.id ?? "");
      const searchParams = new URLSearchParams(globalThis.window.location.search);
      const response = await fetch("/api/template-journey", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          adult_count: values.adult_count,
          child_count: values.child_count,
          childAges: childAgesAsNumbers,
          room_count: values.room_count,
          closest_city: originId,
          duration: values.duration,
          startDate: formatDashedYYYYMMDD(values.startDate),
          endDate: values.endDate ? formatDashedYYYYMMDD(values.endDate) : null,
          twinRoomPreferred: false,
          journeyId: tripOverview.tripId,
          originLocationId: Number(originId),
          buildParameters: {
            closest_city: originId,
            originLocationId: Number(originId),
            journeyTitleOverride: searchParams.get("title") || undefined
          },
          requestSource: searchParams.get("template") === "1" ? buildRequestSource({
            environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
            source: searchParams.get("source"),
            lp: searchParams.get("lp"),
            action: "create"
          }) : stringifySource({
            environment: process.env.NEXT_PUBLIC_SOURCE_ENV,
            name: sourceName ?? "blueprint",
            action: "edit"
          }),
          useYearRoundPricingCache: false
        })
      }).catch(_temp6);
      if (!response?.ok) {
        return {
          error: tFeedback("error_generic")
        };
      }
      const responseData = await response.json();
      const {
        failureMessages,
        uniqueId: newTripId,
        customerInfoMessages
      } = responseData.data;
      if (failureMessages?.length) {
        const code = failureMessages[0] as JOURNEY_EDIT_ERROR;
        return {
          error: getEditErrorMessage(code) || tFeedback("error_generic")
        };
      }
      const currentPrice = utils.trip.getPrice.getData({
        tripId
      });
      if (currentPrice) {
        utils.trip.getPrice.setData({
          tripId: newTripId
        }, currentPrice, {
          updatedAt: 0
        });
      }
      if (!("closest_city" in overrides)) {
        const heroRoutesCms = queryClient.getQueryData(makeHeroRoutesCmsKey(tripId));
        if (heroRoutesCms) {
          queryClient.setQueryData(makeHeroRoutesCmsKey(newTripId), heroRoutesCms);
        }
        const placeImagesCms = queryClient.getQueryData(makePlaceImagesCmsKey(tripId));
        if (placeImagesCms) {
          queryClient.setQueryData(makePlaceImagesCmsKey(newTripId), placeImagesCms);
        }
      }
      await Promise.allSettled([utils.trip.getOverview.fetch({
        tripId: newTripId
      }), utils.trip.getPrice.fetch({
        tripId: newTripId
      }), utils.cms.origins.fetch({
        tripId: newTripId
      })]);
      const nextUrl = globalThis.window.location.pathname.replace(tripId, newTripId);
      if ("closest_city" in overrides) {
        preserveScrollOnEnter(nextUrl);
        startTransition(() => {
          setActiveTripId(newTripId);
          router.replace(nextUrl, {
            scroll: false
          });
        });
      } else {
        globalThis.window.history.replaceState({
          [REWRITTEN_TRIP_ID_HISTORY_KEY]: newTripId
        }, "", nextUrl);
        const bookingEl = globalThis.document.getElementById("overview-booking-details");
        const beforeTop = bookingEl?.getBoundingClientRect().top ?? null;
        flushSync(() => setActiveTripId(newTripId));
        if (bookingEl && beforeTop !== null) {
          let stableFrames = 0;
          let totalFrames = 0;
          let aborted = false;
          const abort = () => {
            aborted = true;
          };
          const listenerOpts = {
            passive: true,
            capture: true
          } as const;
          const stop = () => {
            globalThis.window.removeEventListener("wheel", abort, true);
            globalThis.window.removeEventListener("touchmove", abort, true);
          };
          globalThis.window.addEventListener("wheel", abort, listenerOpts);
          globalThis.window.addEventListener("touchmove", abort, listenerOpts);
          const hold = () => {
            if (aborted) {
              stop();
              return;
            }
            const delta = bookingEl.getBoundingClientRect().top - beforeTop;
            const maxScroll = globalThis.document.documentElement.scrollHeight - globalThis.window.innerHeight;
            const target = Math.max(0, Math.min(globalThis.window.scrollY + delta, maxScroll));
            if (Math.abs(target - globalThis.window.scrollY) > 1) {
              globalThis.window.scrollTo({
                top: target,
                behavior: "instant"
              });
              stableFrames = 0;
            } else {
              stableFrames = stableFrames + 1;
              stableFrames;
            }
            totalFrames = totalFrames + 1;
            totalFrames;
            if (stableFrames < 4 && totalFrames < 90) {
              globalThis.requestAnimationFrame(hold);
            } else {
              stop();
            }
          };
          hold();
        }
      }
      utils.account.isTripSaved.invalidate({
        tripId: newTripId
      });
      return {
        tripId: newTripId,
        customerInfoMessages: toCustomerInfoMessages(customerInfoMessages)
      };
    };
    $[10] = getEditErrorMessage;
    $[11] = origins;
    $[12] = queryClient;
    $[13] = router;
    $[14] = setActiveTripId;
    $[15] = sourceName;
    $[16] = tFeedback;
    $[17] = tripId;
    $[18] = tripOverview;
    $[19] = utils;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
function _temp(s) {
  return s.setActiveTripId;
}
function _temp2(m) {
  return m.childAges;
}
function _temp3(i) {
  return i.type === "leg";
}
function _temp4(sum, m_0) {
  return sum + m_0.adultCount;
}
function _temp5(age) {
  return !Number.isNaN(age);
}
function _temp6() {
  return null;
}